import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, Save, Trash2, Database } from 'lucide-react'
import { useArtwork, useUpdateArtwork, useDeleteArtwork } from '../hooks/useArtworks'
import { BasicInfoForm } from '../components/BasicInfoForm'
import { LoadingScreen } from '@/components/common/feedback/LoadingScreen'
import { artworkApi } from '../api/artworkApi'
import { StatusActions } from '../components/StatusActions'
import { StatusBadge } from '@/components/common/data-display/StatusBadge'
import { CommissionsView } from '../components/CommissionsView'
import type { UpdateArtworkData } from '../api/artworkApi'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { UserRole } from '@/types/entities'
import { ArtworkConceptsPanel } from '@/features/knowledge'

export function ArtworkDetailPage() {
  const { artworkId } = useParams<{ artworkId: string }>()
  const navigate = useNavigate()
  const manager = useAuthStore((s) => s.user?.role === UserRole.MANAGER)
  const [isEditing, setIsEditing] = useState(false)

  const { data: artwork, isLoading } = useArtwork(Number(artworkId))
  const { mutate: updateArtwork } = useUpdateArtwork()
  const { mutate: deleteArtwork } = useDeleteArtwork()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!artwork) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Artwork not found</h2>
          <Button onClick={() => navigate('/app/artworks')} className="mt-4">
            Back to Artworks
          </Button>
        </div>
      </div>
    )
  }

  const handleSave = (data: UpdateArtworkData) => {
    updateArtwork(
      { id: artwork.id, data },
      {
        onSuccess: () => setIsEditing(false),
      }
    )
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this artwork?')) {
      deleteArtwork(artwork.id, {
        onSuccess: () => navigate('/app/artworks'),
      })
    }
  }

  return (
    <div className="space-y-8 p-4 sm:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/artworks')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="min-w-0 break-words font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{artwork.name}</h1>
              <StatusBadge status={artwork.status} needsReview={artwork.reviewHold} />
            </div>
            <p className="text-muted-foreground text-base">Manage your artwork details</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {manager && artwork.status === 'PUBLISHED' && (
            <Button
              variant="outline"
              onClick={() => navigate(`/app/content-studio?view=ideas&artwork=${artwork.id}`)}
            >
              Create world & film
            </Button>
          )}
          <StatusActions artwork={artwork} />
          {/* Developer: Firestore Console Link */}
          {import.meta.env.DEV && artwork._firestoreConsoleUrl && (
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/50 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 hover:text-amber-800 shadow-subtle"
              onClick={() => window.open(artwork._firestoreConsoleUrl, '_blank')}
            >
              <Database className="mr-2 h-4 w-4" />
              Firestore
            </Button>
          )}
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="shadow-subtle"
              >
                Cancel
              </Button>
              <Button form="artwork-form" className="shadow-subtle">
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleDelete} className="shadow-subtle">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
              <Button
                type="button"
                onClick={(event) => {
                  // This node becomes Save Changes during the click. Cancel the browser's
                  // default action so it cannot submit the newly associated form immediately.
                  event.preventDefault()
                  setIsEditing(true)
                }}
                className="shadow-subtle"
              >
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* A held piece looked exactly like one waiting its turn, so artists waited. Say that it is
          held, and why, where they will see it. */}
      {artwork.reviewHold && (
        <div
          role="status"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/20 dark:text-red-200"
        >
          <p className="font-medium">This artwork needs review before it can be published.</p>
          <p className="mt-1">
            {artwork.reviewNote
              ? artwork.reviewNote
              : 'Our team has a question about this piece and has put it on hold.'}
          </p>
          <p className="mt-1 text-red-800/80 dark:text-red-200/80">
            Reply to support@myarthaus.com with the artwork title if you can help us clear it.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Image Preview */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Artwork Image</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={artworkApi.getArtworkImageUrl(artwork.id)}
                alt={artwork.name}
                className="w-full h-auto rounded-lg"
                onError={(e) => {
                  // Prevent infinite loop by checking if we're already showing the fallback
                  if (e.currentTarget.src.includes('placeholder-image')) {
                    return
                  }
                  e.currentTarget.src = '/placeholder-image.svg'
                }}
              />
            </CardContent>
          </Card>
          {manager && (
            <div className="mt-6">
              <ArtworkConceptsPanel artworkId={artwork.id} />
            </div>
          )}
        </div>

        {/* Right: Details Tabs */}
        <div>
          <Tabs defaultValue="basic">
            <TabsList className="w-full">
              <TabsTrigger value="basic" className="flex-1">
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="commissions" className="flex-1">
                Commissions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="mt-4">
              <Card>
                <CardContent className="pt-6">
                  <BasicInfoForm artwork={artwork} readOnly={!isEditing} onSave={handleSave} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="commissions" className="mt-4">
              <Card>
                <CardContent className="pt-6">
                  <CommissionsView artworkId={artwork.id} status={artwork.status} readOnly={!isEditing} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}
